import { useMutation, useQuery } from "@apollo/client";
import {
  APPROVE_SUBMISSION_DOCUMENT_REVIEW_MUTATION,
  APPROVE_SUBMISSION_REVIEW_MUTATION,
  BULK_DELETE_SUBMISSIONS_MUTATION,
  CREATE_SUBMISSION_MUTATION,
  DELETE_SUBMISSION_MUTATION,
  GET_SUBMISSION_QUERY,
  MOVE_TO_REVIEW_MUTATION,
  REJECT_SUBMISSION_DOCUMENT_REVIEW_MUTATION,
  REJECT_SUBMISSION_REVIEW_MUTATION,
  SET_SUBMISSION_STEP_ASSIGNEE_MUTATION,
  SWAP_SUBMISSION_DOCUMENT_MUTATION,
  SUBMISSION_VIRTUAL_EVENTS_QUERY,
  UPDATE_SIGNATURE_STATUS_TO_SENT_MUTATION,
  UPDATE_SUBMISSION_MUTATION,
  UPDATE_SUBMISSION_STATUS_MUTATION,
  VERIFY_AND_SUBMIT_SUBMISSION_MUTATION,
} from "queries/submissions";

export function useCreateSubmission() {
  return useMutation(CREATE_SUBMISSION_MUTATION, {
    refetchQueries: [SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useUpdateSubmission() {
  return useMutation(UPDATE_SUBMISSION_MUTATION, {
    update(cache) {
      // Evict submissionVirtualEvents from cache to force fresh network request
      // This ensures the datagrid shows the updated submission title
      cache.evict({ fieldName: "submissionVirtualEvents" });
      cache.gc(); // Garbage collect to clean up orphaned references
    },
    // Refetch active submission view so local "dirty" baselines update after saving
    // (especially important for document uploads on Filing Complete submissions).
    // Includes the stepper shell query so stepper edits (e.g. a staff
    // override-approve on the Review step) reflect immediately.
    refetchQueries: ["GetSubmissionStepper", GET_SUBMISSION_QUERY, SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useMoveToReview() {
  return useMutation(MOVE_TO_REVIEW_MUTATION, {
    // Include the stepper shell query so the stepper re-renders in its new
    // (in-review) state instead of navigating the user out.
    refetchQueries: ["GetSubmissionStepper", SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useVerifyAndSubmitSubmission() {
  return useMutation(VERIFY_AND_SUBMIT_SUBMISSION_MUTATION, {
    refetchQueries: ["GetSubmissionStepper", SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useSetSubmissionStepAssignee() {
  return useMutation(SET_SUBMISSION_STEP_ASSIGNEE_MUTATION, {
    // Refetch the stepper shell so `stepperSteps.assignee` (the verifier on
    // VERIFY_AND_SUBMIT) updates for the step that renders it.
    refetchQueries: ["GetSubmissionStepper"],
    awaitRefetchQueries: true,
  });
}

export function useApproveSubmissionReview() {
  return useMutation(APPROVE_SUBMISSION_REVIEW_MUTATION, {
    refetchQueries: ["GetSubmissionStepper", SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useRejectSubmissionReview() {
  return useMutation(REJECT_SUBMISSION_REVIEW_MUTATION, {
    refetchQueries: ["GetSubmissionStepper", SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useApproveSubmissionDocumentReview() {
  return useMutation(APPROVE_SUBMISSION_DOCUMENT_REVIEW_MUTATION, {
    refetchQueries: [GET_SUBMISSION_QUERY, SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useRejectSubmissionDocumentReview() {
  return useMutation(REJECT_SUBMISSION_DOCUMENT_REVIEW_MUTATION, {
    refetchQueries: [SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useDeleteSubmission() {
  return useMutation(DELETE_SUBMISSION_MUTATION, {
    refetchQueries: [SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useBulkDeleteSubmissions() {
  return useMutation(BULK_DELETE_SUBMISSIONS_MUTATION, {
    refetchQueries: [SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useGetSubmission(id: string, skip?: boolean) {
  return useQuery(GET_SUBMISSION_QUERY, {
    variables: { id },
    skip: skip || !id,
    fetchPolicy: "cache-and-network",
  });
}

export function useUpdateSubmissionStatus() {
  return useMutation(UPDATE_SUBMISSION_STATUS_MUTATION, {
    refetchQueries: ["GetSubmissionStepper", GET_SUBMISSION_QUERY, SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useSwapSubmissionDocument() {
  return useMutation(SWAP_SUBMISSION_DOCUMENT_MUTATION, {
    refetchQueries: [GET_SUBMISSION_QUERY, SUBMISSION_VIRTUAL_EVENTS_QUERY],
    awaitRefetchQueries: true,
  });
}

export function useUpdateSignatureStatusToSent() {
  return useMutation(UPDATE_SIGNATURE_STATUS_TO_SENT_MUTATION);
}
